import React, { useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Scale, Sparkles, Loader2, X, ArrowDownRight, ArrowUpRight, RotateCcw } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { toast } from "sonner";

const TYPE_LABEL = {
    invoice_payment: "Invoice payment",
    bill_payment: "Bill payment",
    expense: "Expense",
};

export default function Reconciliation() {
    const { currentOrg } = useAuth();
    const [txns, setTxns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [autoBusy, setAutoBusy] = useState(false);
    // Map of txnId → { loading, candidates: [...] }
    const [matches, setMatches] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/bank-transactions");
            setTxns(data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

    const unrec = useMemo(() => txns.filter((t) => !t.reconciled), [txns]);
    const rec = useMemo(() => txns.filter((t) => t.reconciled), [txns]);

    const fetchSuggestions = async (t) => {
        if (matches[t.id]) return; // already loaded / loading
        setMatches((prev) => ({ ...prev, [t.id]: { loading: true, candidates: [] } }));
        try {
            const { data } = await api.get(`/bank-transactions/${t.id}/match-suggestions`);
            setMatches((prev) => ({ ...prev, [t.id]: { loading: false, candidates: data.candidates || [] } }));
        } catch (e) {
            toast.error(formatApiError(e));
            setMatches((prev) => ({ ...prev, [t.id]: { loading: false, candidates: [] } }));
        }
    };

    // Lazy-load suggestions for visible un-reconciled rows so the page is snappy
    // even with hundreds of transactions.
    useEffect(() => {
        if (!unrec.length) return;
        unrec.slice(0, 50).forEach((t) => { fetchSuggestions(t); });
        // eslint-disable-next-line
    }, [txns]);

    const acceptMatch = async (t, c) => {
        try {
            await api.post(`/bank-transactions/${t.id}/reconcile`, {
                category: t.category || "General",
                matched_id: c.id,
                matched_type: c.type,
            });
            toast.success(`Matched · ${c.label}`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const reconcileNoMatch = async (t) => {
        try {
            await api.post(`/bank-transactions/${t.id}/reconcile`, {
                category: t.category || "General",
            });
            toast.success("Marked reconciled (no match)");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const unreconcile = async (t) => {
        try {
            await api.post(`/bank-transactions/${t.id}/unreconcile`, {});
            toast.success("Unreconciled");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const runAutoMatch = async () => {
        setAutoBusy(true);
        try {
            const { data } = await api.post("/bank-transactions/auto-match", {});
            if (data.matched > 0) {
                toast.success(`Auto-matched ${data.matched} of ${data.scanned} transaction${data.scanned === 1 ? "" : "s"}`);
            } else {
                toast.info(`Scanned ${data.scanned} — no high-confidence matches found.`);
            }
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setAutoBusy(false);
        }
    };

    const currency = currentOrg?.currency || "KYD";

    return (
        <div>
            <PageHeader
                subtitle="Banking"
                title="Reconciliation"
                action={
                    <Button
                        onClick={runAutoMatch}
                        disabled={autoBusy || unrec.length === 0}
                        className="bg-ocean-700 text-white rounded-xl"
                        data-testid="auto-match-btn"
                    >
                        {autoBusy ? (<><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Matching…</>)
                                  : (<><Sparkles className="h-4 w-4 mr-1.5" /> Auto-match all</>)}
                    </Button>
                }
            />

            {loading ? (
                <div className="py-20 flex items-center justify-center text-sm text-muted-foreground">
                    <Loader2 className="h-5 w-5 mr-2 animate-spin text-secondary" /> Loading transactions…
                </div>
            ) : txns.length === 0 ? (
                <EmptyState icon={Scale} title="Nothing to reconcile" description="Import bank statements or add manual transactions to get started." />
            ) : (
                <div className="grid lg:grid-cols-2 gap-6">
                    <section data-testid="recon-needs-review">
                        <div className="flex items-center justify-between mb-3">
                            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                                Needs review · {unrec.length}
                            </div>
                            <span className="text-[11px] text-muted-foreground">Suggestions show top match score</span>
                        </div>
                        <div className="rounded-3xl bg-white border border-sand-200 shadow-card divide-y divide-sand-200" data-testid="recon-unrec-list">
                            {unrec.map((t) => {
                                const m = matches[t.id];
                                const top = m?.candidates?.[0];
                                const direction = (t.amount || 0) >= 0 ? "in" : "out";
                                return (
                                    <div key={t.id} className="p-4" data-testid={`recon-row-${t.id}`}>
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                                <div className="font-medium text-ocean-700 truncate flex items-center gap-1.5">
                                                    {direction === "in" ? <ArrowDownRight className="h-3.5 w-3.5 text-secondary-dark shrink-0" /> : <ArrowUpRight className="h-3.5 w-3.5 text-coral-dark shrink-0" />}
                                                    {t.description}
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                                                    <span>{fmtDate(t.date)}</span>
                                                    {t.category && <Badge className="bg-sand-200 text-ocean-700 border-0">{t.category}</Badge>}
                                                    {t.reference && <span>· {t.reference}</span>}
                                                </div>
                                            </div>
                                            <div className={`font-mono shrink-0 ${direction === "out" ? "text-coral-dark" : "text-secondary-dark"}`}>{fmtMoney(t.amount, currency)}</div>
                                        </div>

                                        <div className="mt-3" data-testid={`recon-suggestions-${t.id}`}>
                                            {m?.loading && (
                                                <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                                                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Looking for matches…
                                                </div>
                                            )}
                                            {m && !m.loading && m.candidates.length === 0 && (
                                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                                    <span className="text-xs text-muted-foreground">No automatic match found.</span>
                                                    <Button size="sm" variant="outline" className="rounded-lg" onClick={() => reconcileNoMatch(t)} data-testid={`recon-mark-${t.id}`}>
                                                        <Check className="h-3.5 w-3.5 mr-1" /> Mark reconciled
                                                    </Button>
                                                </div>
                                            )}
                                            {m && !m.loading && m.candidates.length > 0 && (
                                                <div className="space-y-1.5">
                                                    {m.candidates.slice(0, 3).map((c, idx) => {
                                                        const isTop = idx === 0;
                                                        const pct = Math.round((c.score || 0) * 100);
                                                        return (
                                                            <div
                                                                key={`${c.type}:${c.id}`}
                                                                className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${isTop ? "border-secondary/40 bg-secondary/5" : "border-sand-200"}`}
                                                                data-testid={`recon-cand-${t.id}-${c.id}`}
                                                            >
                                                                <div className="min-w-0 flex-1">
                                                                    <div className="text-sm text-ocean-700 truncate">
                                                                        {c.label}
                                                                    </div>
                                                                    <div className="text-[11px] text-muted-foreground">
                                                                        {TYPE_LABEL[c.type] || c.type} · {fmtDate(c.date)} · {fmtMoney(c.amount, c.currency || currency)}
                                                                    </div>
                                                                </div>
                                                                <Badge className={`shrink-0 border-0 ${pct >= 85 ? "bg-emerald-100 text-emerald-700" : pct >= 70 ? "bg-amber-100 text-amber-700" : "bg-sand-200 text-ocean-700"}`}>
                                                                    {pct}%
                                                                </Badge>
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => acceptMatch(t, c)}
                                                                    className="rounded-lg bg-ocean-700 text-white shrink-0"
                                                                    data-testid={`recon-accept-${t.id}-${c.id}`}
                                                                >
                                                                    <Check className="h-3.5 w-3.5 mr-1" /> Match
                                                                </Button>
                                                            </div>
                                                        );
                                                    })}
                                                    {top && (
                                                        <button
                                                            type="button"
                                                            className="text-xs text-muted-foreground hover:text-ocean-700 mt-1.5 inline-flex items-center gap-1"
                                                            onClick={() => reconcileNoMatch(t)}
                                                            data-testid={`recon-skip-${t.id}`}
                                                        >
                                                            <X className="h-3.5 w-3.5" /> None of these — mark reconciled anyway
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            {!m && (
                                                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => fetchSuggestions(t)} data-testid={`recon-find-${t.id}`}>
                                                    <Sparkles className="h-3.5 w-3.5 mr-1" /> Find matches
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                            {unrec.length === 0 && <div className="p-10 text-center text-sm text-muted-foreground">All caught up. Nothing to review.</div>}
                        </div>
                    </section>

                    <section data-testid="recon-reconciled">
                        <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
                            Reconciled · {rec.length}
                        </div>
                        <div className="rounded-3xl bg-white border border-sand-200 shadow-card divide-y divide-sand-200" data-testid="recon-rec-list">
                            {rec.slice(0, 60).map((t) => (
                                <div key={t.id} className="p-4 flex items-center justify-between gap-3" data-testid={`recon-rec-row-${t.id}`}>
                                    <div className="min-w-0">
                                        <div className="text-sm font-medium text-ocean-700 truncate">{t.description}</div>
                                        <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap mt-0.5">
                                            <span>{fmtDate(t.date)}</span>
                                            {t.category && <Badge className="bg-sand-200 text-ocean-700 border-0">{t.category}</Badge>}
                                            {t.matched_type && (
                                                <Badge className="bg-emerald-50 text-emerald-700 border-0 text-[10px] uppercase tracking-wider">
                                                    {TYPE_LABEL[t.matched_type] || t.matched_type}
                                                </Badge>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className={`font-mono text-sm ${(t.amount || 0) < 0 ? "text-coral-dark" : "text-secondary-dark"}`}>{fmtMoney(t.amount, currency)}</span>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => unreconcile(t)}
                                            title="Unreconcile"
                                            data-testid={`recon-undo-${t.id}`}
                                        >
                                            <RotateCcw className="h-3.5 w-3.5 text-muted-foreground" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                            {rec.length === 0 && <div className="p-10 text-center text-sm text-muted-foreground">Nothing reconciled yet.</div>}
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
}
